import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Roles } from 'src/app/core/services/roles.service';
import { ITokens } from '../../modules/authorization/store/types';
import { IPaymentAuth } from '../../modules/logged/payrolls/store/types';
import { Observable, ReplaySubject, Subject, of, timer } from 'rxjs';
import { catchError, filter, map, take, timeout } from 'rxjs/operators';
import * as Sentry from '@sentry/browser';

export interface ITokenData {
    exp: number;
    iat: number;
    data: {
        _id: string;
        email: string;
        role: Roles;
        company?: string;
    };
}
export const TOKENS = 'tokens';
export const TOKEN = 'token';
export const REFRESH_TOKEN = 'refreshToken';
export const PAYMENTS_TOKEN = 'paymentsToken';

@Injectable({
    providedIn: 'root',
})
export class TokenService {
    constructor() {}

    // local tokens
    public getTokens(): ITokens[] {
        let tokens = [];
        try {
            tokens = JSON.parse(localStorage.getItem(TOKENS)) || [];
        } catch (e) {
            tokens = [];
        }
        return tokens;
    }

    public addToken(auth: ITokens): void {
        const tokens = this.getTokens();
        if (!tokens.map((el) => el.token).includes(auth.token) && !tokens.map((el) => el.refresh_token).includes(auth.refresh_token)) {
            tokens.push(auth);
            localStorage.setItem(TOKENS, JSON.stringify(tokens));
        }
    }

    public deleteToken(auth: ITokens): void {
        const tokens = this.getTokens().filter((el) => el.token !== auth.token && el.refresh_token !== auth.refresh_token);
        localStorage.setItem(TOKENS, JSON.stringify(tokens));
    }

    // session token
    public saveSessionToken(token: string): void {
        sessionStorage.setItem(TOKEN, token);
    }

    public removeSessionToken(): void {
        sessionStorage.removeItem(TOKEN);
    }

    public getSessionToken(): string {
        return sessionStorage.getItem(TOKEN);
    }

    // session refresh token
    public saveSessionRefreshToken(refreshToken: string): void {
        sessionStorage.setItem(REFRESH_TOKEN, refreshToken);
    }
    public getSessionRefreshToken(): string {
        return sessionStorage.getItem(REFRESH_TOKEN);
    }

    public removeSessionRefreshToken(): void {
        sessionStorage.removeItem(REFRESH_TOKEN);
    }

    public getTokenData(token: string): ITokenData {
        if (token) {
            const helper = new JwtHelperService();
            const data: ITokenData = helper.decodeToken(token);
            return data;
        }
        return null;
    }

    public getPaymentsToken(): IPaymentAuth {
        let token;
        try {
            token = JSON.parse(sessionStorage.getItem(PAYMENTS_TOKEN)) || null;
        } catch (e) {
            token = null;
        }
        return token;
    }

    private paymentTokenSubject = new ReplaySubject<IPaymentAuth | null>(1);

    public get getPaymentsTokenObs(): Observable<IPaymentAuth> {
        return this.paymentTokenSubject.asObservable().pipe(filter((v) => !!v));
    }
    public addPaymentsToken(authPayments: IPaymentAuth): void {
        console.log('addPaymentsToken', authPayments);
        Sentry.addBreadcrumb({
            category: 'payroll authPayments',
            message: 'add payments token ' + JSON.stringify(authPayments),
            level: 'info',
        });
        this.paymentTokenSubject.next(authPayments);
        sessionStorage.setItem(PAYMENTS_TOKEN, JSON.stringify(authPayments));
    }
    public removePaymentsToken(): void {
        console.log('removePaymentsToken');
        Sentry.addBreadcrumb({
            category: 'payroll authPayments',
            message: 'removePaymentsToken',
            level: 'info',
        });
        this.paymentTokenSubject.next(null);
        sessionStorage.removeItem(PAYMENTS_TOKEN);
    }

    public isLoggedAsCompany(): boolean {
        const tokens = this.getTokens();
        const sessionToken = this.getSessionToken();
        const sessionRefreshToken = this.getSessionRefreshToken();
        return !tokens.find((token) => token.token === sessionToken && token.refresh_token === sessionRefreshToken);
    }
}
